import authorsData from "~/content/data/authors.json";

export interface Author {
  key: string;
  name: string;
  title: string;
  image: string;
  /** Short biography shown on the author's page. Empty when not yet written. */
  bio: string;
  /** Public LinkedIn profile, used as schema.org `sameAs`. Empty when unknown. */
  linkedin: string;
}

/** Path to an author's page. Keyed by the same slug used in frontmatter. */
export function authorPath(key: string): string {
  return `/authors/${key}`;
}

/**
 * Whether this byline should link to an author page.
 *
 * Two cases must not: the collective C1 byline, which is the organization
 * rather than a person, and outside contributors on guides, who have no
 * record in authors.json and are synthesized by `resolveAuthors` below.
 */
export function hasAuthorPage(key: string): boolean {
  return key !== TEAM_AUTHOR_KEY && getAuthor(key) !== undefined;
}

type AuthorKey = keyof typeof authorsData;

export function getAuthor(key: string): Author | undefined {
  const a = authorsData[key as AuthorKey] as
    | { name: string; title: string; image: string; bio?: string; linkedin?: string }
    | undefined;
  if (!a) return undefined;
  return { key, name: a.name, title: a.title, image: a.image, bio: a.bio ?? "", linkedin: a.linkedin ?? "" };
}

export function getAuthors(keys: string[]): Author[] {
  return keys.map(getAuthor).filter((a): a is Author => a !== undefined);
}

/**
 * Key of the collective C1 byline in authors.json. Used for content written by
 * the company rather than a named person — structured data credits the
 * organization instead of a Person for these.
 */
export const TEAM_AUTHOR_KEY = "c1-team";

/**
 * Like `getAuthors`, but keeps entries that aren't in authors.json by treating
 * the raw frontmatter value as a display name. Guides carry outside-contributor
 * bylines ("James Walker", "Sooter Saalu") that have no author record, and
 * dropping them silently would lose the attribution.
 */
export function resolveAuthors(entries: string[]): Author[] {
  return entries
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0)
    .map(
      (entry) =>
        getAuthor(entry) ?? {
          key: entry.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""),
          name: entry,
          title: "",
          image: "",
          bio: "",
          linkedin: "",
        },
    );
}

/** Every author key that has a record in authors.json, excluding the C1 byline. */
export function allAuthorKeys(): string[] {
  return Object.keys(authorsData).filter((k) => k !== TEAM_AUTHOR_KEY);
}
